import { useState, useRef, useEffect } from "react";
import getConfig from "next/config";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Keyboard, Thumbs } from "swiper/modules";
import classNames from "classnames";
import "swiper/css";
import "swiper/css/zoom";
import "swiper/css/pagination";

import CarouselNavArrow from "@components/Icons/CarouselNavArrow";
import { resizeImage } from "@lib/helpers";
import DotIcon from "@components/Icons/DotIcon";

const {
  publicRuntimeConfig: { settings },
} = getConfig();

const ProductGallery = ({
  images,
  title,
  onSlideClick,
  onSlideChange,
  currentSlide,
  setMasterSwiper,
}) => {
  const singleSlide = images.length <= 1;
  const mainSwiper = useRef(null);
  const thumbSwiperRef = useRef(null);
  const firstMount = useRef(true);

  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [prevEl, setPrevEl] = useState(null);
  const [nextEl, setNextEl] = useState(null);

  useEffect(() => {
    if (mainSwiper.current) {
      setMasterSwiper(mainSwiper.current);
    }
  }, [mainSwiper.current]);

  useEffect(() => {
    if (
      mainSwiper?.current &&
      mainSwiper.current.activeIndex !== currentSlide
    ) {
      mainSwiper.current.slideTo(currentSlide);
    }

    if (
      thumbSwiperRef?.current &&
      thumbSwiperRef.current.activeIndex !== currentSlide
    ) {
      thumbSwiperRef.current.slideTo(currentSlide);
    }
  }, [currentSlide]);

  return (
    <div className="flex flex-col-reverse sm:flex-row gap-[24px] justify-around w-full relative">
      {/* thumbnail slider */}

      <div className="my-auto relative max-h-[440px]">
        <Swiper
          className="sm:h-[300px] my-auto max-w-[80px] sm:max-w-full sm:overflow-scroll sm:scrollbar-none"
          direction="vertical"
          onSwiper={(swiper) => {
            thumbSwiperRef.current = swiper;
          }}
          loop={false}
          initialSlide={currentSlide}
          modules={[Navigation, Thumbs]}
          onSlideChange={({ isBeginning, isEnd, activeIndex }) => {
            setIsBeginning(isBeginning);
            setIsEnd(isEnd);

            if (firstMount.current) {
              firstMount.current = false;
            } else {
              onSlideChange(activeIndex);
            }
          }}
          // loopAddBlankSlides={false}
          breakpoints={{
            0: {
              direction: "horizontal",
              slidesPerView: 5,
              centeredSlides: true,
              centeredSlidesBounds: false,
              slidesPerGroup: 1,
              spaceBetween: 0,
              slideToClickedSlide: true,
            },
            640: {
              direction: "vertical",
              slidesPerView: 5,
              slidesPerGroup: 1,
              centeredSlides: true,
              centeredSlidesBounds: true,
              resistanceRatio: 0,
              spaceBetween: 0,
            },
          }}
        >
          {images.map((image, index) => (
            <SwiperSlide
              key={index}
              className="h-full sm:!h-[60px] sm:w-[60px]"
            >
              <div
                className={classNames(
                  "sm:hidden flex items-center justify-center w-[20px] h-[20px]",
                  {
                    "text-[#C7C7C7]": currentSlide !== index,
                    "text-black": currentSlide === index,
                  }
                )}
              >
                <DotIcon
                  width={currentSlide === index ? "12px" : "6px"}
                  height={currentSlide === index ? "12px" : "6px"}
                  className="transition-all duration-200"
                />
              </div>

              <div
                className={`hidden sm:block p-[6px] rounded-bl-lg rounded-tr-lg cursor-pointer select-none ${
                  currentSlide === index ? "border border-yellow" : ""
                }`}
              >
                <img
                  src={resizeImage(image.file, "50x50")}
                  className="block aspect-square object-contain select-none"
                  alt=""
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {!singleSlide && (
          <>
            <div className="hidden sm:block absolute left-[50%] translate-x-[-50%] z-10 -top-[40px]">
              <PreviousButton setPrevEl={setPrevEl} isBeginning={isBeginning} />
            </div>

            <div className="hidden sm:block absolute left-[50%] translate-x-[-50%] z-10 -bottom-[40px]">
              <NextButton setNextEl={setNextEl} isEnd={isEnd} />
            </div>
          </>
        )}
      </div>

      {/* main image slider */}
      <Swiper
        className="w-full sm:w-[calc(100%-80px)] sm:min-h-[440px]"
        onSwiper={(swiper) => (mainSwiper.current = swiper)}
        initialSlide={currentSlide}
        slidesPerView={1}
        spaceBetween={10}
        loop={false}
        // lazy={true}
        keyboard={true}
        thumbs={{ swiper: thumbSwiperRef.current }}
        modules={[Thumbs, Navigation, Keyboard]}
        onSlideChange={({ activeIndex, isBeginning, isEnd }) => {
          onSlideChange(activeIndex);
          setIsBeginning(isBeginning);
          setIsEnd(isEnd);
        }}
        navigation={{
          nextEl,
          prevEl,
        }}
      >
        {images.map((image, index) => (
          <SwiperSlide className="cursor-pointer" key={index}>
            <div className="swiper-zoom-container mx-auto aspect-square w-full">
              <img
                onClick={onSlideClick}
                src={`${settings.cdn}${image.file}`}
                alt={title}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

const PreviousButton = ({ setPrevEl, isBeginning }) => (
  <div
    ref={(node) => setPrevEl(node)}
    className={`w-8 h-8 rounded-full bg-[#EFF6FF] ${
      isBeginning ? "opacity-50 cursor-default" : "opacity-90 cursor-pointer"
    }`}
  >
    <CarouselNavArrow direction="up" />
  </div>
);

const NextButton = ({ setNextEl, isEnd }) => (
  <div
    ref={(node) => setNextEl(node)}
    className={`w-8 h-8 rounded-full bg-[#EFF6FF] ${
      isEnd ? "opacity-50 cursor-default" : "opacity-90 cursor-pointer"
    }`}
  >
    <CarouselNavArrow direction="down" />
  </div>
);

export default ProductGallery;
